import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFormType } from '@neo/blocks/forms/types'
import { SheetFooter } from '@neo/primitives/sheet'
import { Typography } from '@neo/primitives/typography'
import { useState } from 'react'
import { useRegisterCustomMcpMutation } from 'v2/pages/settings/integrations/queries'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import {
  type CustomMcpFormValues,
  customMcpDefaultValues,
  customMcpFormFields,
  customMcpFormSchema,
  describeHeader,
} from './custom-mcp-form.config'
import { McpIconField } from './mcp-icon-field'

interface CustomMcpFormProps {
  onConnected: () => void
}

const FORM_ID = 'integration-custom-mcp-form'

/**
 * What the bridge will send, masked. The inputs alone can't show this: `basic` becomes a base64
 * blob and `bearer` gets wrapped in a header the user never types, so this is where a mistyped
 * header name or a password pasted into the username box becomes visible — before connecting,
 * rather than as a bare 401 afterwards.
 */
const HeaderPreview = ({ form }: { form: GenericFormType<CustomMcpFormValues> }) => {
  // form.useStore, not form.Subscribe: this repo's react-form types the Subscribe selector as
  // returning FormState, and `.useStore` is the pattern the other configs here use.
  const values = form.useStore((state) => state.values)
  const header = describeHeader(values)
  if (!header) return null

  return (
    <div
      className="mt-4 rounded-md border border-secondary bg-muted px-3 py-2.5 animate-in fade-in duration-200"
      data-testid="mcp-header-preview"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-jetbrains text-label uppercase text-tertiary">Sends</span>
        <span className="break-all font-jetbrains text-xs-400 text-foreground">
          {header.name}: {header.value}
        </span>
      </div>
      {header.note && (
        <p className="mt-1 break-all font-inter text-xs-400 text-tertiary">{header.note}</p>
      )}
    </div>
  )
}

export const CustomMcpForm = ({ onConnected }: CustomMcpFormProps) => {
  const { mutate: registerCustomMcp, isPending: isSubmitting } = useRegisterCustomMcpMutation()
  // Plain state, not a form field: `GenericForm`'s custom-field path does not fire
  // `afterOnChange` (root doc, failure mode 22).
  const [icon, setIcon] = useState<string | null>(null)
  // onMount is the form library's sanctioned way to reach the instance from outside; the preview
  // needs to re-render on every keystroke, which only a subscription gives us.
  const [form, setForm] = useState<GenericFormType<CustomMcpFormValues> | null>(null)

  const handleSubmit = (values: CustomMcpFormValues) =>
    registerCustomMcp({ ...values, icon }, { onSuccess: onConnected })

  return (
    <>
      <div className="flex-grow overflow-y-auto pt-4">
        <div className="mb-4">
          <McpIconField value={icon} onChange={setIcon} />
        </div>
        {/* `GenericForm`'s default `gap-2` crowds each label; anything wider than 16px pushes the
            form past the sheet. */}
        <GenericForm<CustomMcpFormValues>
          className="gap-4"
          id={FORM_ID}
          fields={customMcpFormFields}
          defaultValues={customMcpDefaultValues}
          formValidators={{ onSubmit: customMcpFormSchema }}
          onSubmit={handleSubmit}
          onMount={setForm}
          includeSubmitButton={false}
        >
          {form && <HeaderPreview form={form} />}
        </GenericForm>
        <Typography variant="XS/400" className="mt-4 text-tertiary">
          Credentials are sent only to this server, and stored encrypted.
        </Typography>
      </div>
      <SheetFooter className="mt-6">
        <FormSubmitButton
          formId={FORM_ID}
          loading={isSubmitting}
          className="w-full"
          data-testid="button-connect-mcp"
        >
          {isSubmitting ? 'Connecting…' : 'Connect MCP server'}
        </FormSubmitButton>
      </SheetFooter>
    </>
  )
}
